import { axiosDelete, axiosGet, axiosPatch, axiosPost } from '@/api/handlers';
import { type UseQueryResult, useMutation, useQuery } from '@/api/hooks';
import {
  type BulkUpdateUsersSchema,
  UserFormSchema,
  UserModel,
  UserOptionModel,
  UsersLightModel,
  UsersListModel,
  UsersWithUniqueOrganizationListModel,
} from '@/features/tenant-manager/user/_models/user';
import { useInvalidateQuery } from '@/hooks';
import { dateTimeAtRequestFormat } from '@/utils';
import { useCallback, useMemo } from 'react';
import { z } from 'zod';
import { invalidateListMap, queryKeyMap } from '../../_utils/query-keys';
import { parseUserOptionLabel } from '../_utils/user';

type FormValues = z.infer<typeof UserFormSchema>;

type UserFilterParams = (
  | {
      filterBy: 'pagination';
      page: number;
      limit: number;
      name?: string;
      email?: string;
      organizationIds?: string[];
    }
  | { filterBy: 'ids'; ids: string[] }
) & {
  historySavedAt?: string | null;
  showMigrated?: true;
  showDeleted?: boolean;
};

/**
 * We have added PAGINATION to retrieve 10. You can use datetimeAt.
 *
 * If the number of entries is more than 10, for example, to narrow down the ids, pass "all" to params.
 * @attention To fetch all user, useAllUsers.
 */
export const useFilteredUsers = <U = z.infer<typeof UsersListModel>>({
  params,
  select,
  enabled = true,
  options,
}: {
  params: UserFilterParams;
  select?: (data: z.infer<typeof UsersListModel>) => U;
  options?: {
    ignoreNotJoinedOrganization?: boolean;
  };
  enabled?: boolean;
}): UseQueryResult<U, unknown> => {
  const { historySavedAt, showMigrated } = params;

  const dateTimeAt = dateTimeAtRequestFormat(historySavedAt ?? undefined);

  const requestParams = {
    showDeleted: params.showDeleted,
    all: params.filterBy === 'ids',
    ids: params.filterBy === 'ids' ? params.ids.join(',') : undefined,
    page: params.filterBy === 'pagination' ? params.page : undefined,
    pageSize: params.filterBy === 'pagination' ? params.limit : undefined,
    name: params.filterBy === 'pagination' ? params.name : undefined,
    email: params.filterBy === 'pagination' ? params.email : undefined,
    datetimeAt: dateTimeAt,
    ...(params.filterBy === 'pagination' &&
      !!params.organizationIds?.length && {
        organizationIds: params.organizationIds.join(','),
      }),
    ...(!showMigrated && { isMigrated: false }),
  };

  return useQuery({
    queryKey: queryKeyMap.users(requestParams),
    queryFn: async () => {
      const res = await axiosGet('/api/users/', requestParams);
      return UsersListModel.parse(res);
    },
    select: (data: z.infer<typeof UsersListModel>) => {
      const { ignoreNotJoinedOrganization } = options ?? {};
      const formattedData = ignoreNotJoinedOrganization
        ? {
            ...data,
            results: data.results.filter(
              (user) => user.organizationIds?.length,
            ),
          }
        : data;
      return select ? select(formattedData) : (formattedData as U);
    },
    enabled:
      enabled && (params.filterBy === 'pagination' || !!params.ids?.length),
  });
};

/**
 * For all user acquisition. Skip slow Django processing and use only SQL to speed up response time.
 * @attention You can't use datetimeAt.
 */
export const useAllUsers = <U = z.infer<typeof UsersLightModel>>({
  params: _params,
  select,
}: {
  params?: {
    showMigrated?: true;
    showDeleted?: boolean;
    snapshotId?: string;
  };
  select?: (data: z.infer<typeof UsersLightModel>) => U;
} = {}) => {
  const params = {
    showDeleted: _params?.showDeleted ?? undefined,
    ...(!_params?.showMigrated && { isMigrated: false }),
  };

  return useQuery({
    queryKey: queryKeyMap.usersLight(params),
    queryFn: async () => {
      const users = await axiosGet('/api/users/light/', params);
      return UsersLightModel.parse(users);
    },
    select,
  });
};

type UsersOptionsReturnType = {
  value: string;
  label: string;
  organizationIds: string[];
}[];

export const useUserOptionsForSelectField = ({
  params,
  options,
}: {
  params?: { snapshotId?: string };
  options?: {
    pickUpUserIds?: string[];
    organizationIds?: string[];
  };
}): UseQueryResult<UsersOptionsReturnType> => {
  return useQuery({
    queryKey: queryKeyMap.userOptions(params),
    queryFn: async () => {
      const res = await axiosGet('/api/users/options/', params);
      return UserOptionModel.parse(res);
    },
    select: (data) => {
      const { pickUpUserIds, organizationIds } = options ?? {};
      return data.flatMap((user) => {
        const isUserNotInFilter =
          !!pickUpUserIds && !pickUpUserIds.includes(user.id);

        const isNotInOrganization =
          !user.organizationIds?.length ||
          (!!organizationIds?.length &&
            user.organizationIds.every((id) => !organizationIds.includes(id)));

        return isUserNotInFilter || isNotInOrganization
          ? []
          : {
              value: user.id,
              label: parseUserOptionLabel({
                name: user.name,
                organizations: user.organizations,
              }),
              organizationIds: user.organizationIds ?? [],
            };
      });
    },
  });
};

export const useUser = (id: string | undefined) => {
  return useQuery({
    queryKey: queryKeyMap.user(id),
    queryFn: async () => {
      const user = await axiosGet(`/api/users/${id}/`);
      return UserModel.parse(user);
    },
    enabled: !!id,
  });
};

export type UseUsersWithUniqueOrganizationParams = {
  referenceFiscalYear: string;
  showDeleted?: boolean;
  name?: string | null;
  organizationIds?: string[];
  page?: number;
  pageSize?: number;
};

export const useUsersWithUniqueOrganization = ({
  params,
}: {
  params: UseUsersWithUniqueOrganizationParams;
}) => {
  return useQuery({
    queryKey: queryKeyMap.usersWithUniqueOrganization(params),
    queryFn: async () => {
      const { showDeleted = false, organizationIds, ...rest } = params;
      const users = await axiosGet('/api/users/with-unique-organization/', {
        ...rest,
        showDeleted,
        ...(organizationIds?.length && {
          organizationIds: organizationIds.join(','),
        }),
      });
      return UsersWithUniqueOrganizationListModel.parse(users);
    },
  });
};

export const useUpdateUser = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (data: FormValues & { id: string }) => {
      const parsed = UserFormSchema.extend({ id: z.string() }).parse(data);
      const { id, ...payload } = parsed;
      return await axiosPatch(`/api/users/${id}/`, payload);
    },
    onSuccess: async (_, { id }) => {
      await Promise.all([
        ...invalidateListMap.users().map(async (invalidateList) => {
          await invalidate(invalidateList);
        }),
        invalidate(queryKeyMap.user(id)),
        invalidate(queryKeyMap.organization()),
      ]);
    },
  });
};

export const useCreateUser = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (data: FormValues) => {
      const parsed = UserFormSchema.parse(data);
      return await axiosPost('/api/users/', parsed);
    },
    onSuccess: async () => {
      await Promise.all([
        ...invalidateListMap.users().map(async (invalidateList) => {
          await invalidate(invalidateList);
        }),
        invalidate(queryKeyMap.organization()),
      ]);
    },
  });
};

export const useDeleteUser = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (id: string) => {
      return await axiosDelete(`/api/users/${id}/`);
    },
    onSuccess: async () => {
      await Promise.all([
        ...invalidateListMap.users().map(async (invalidateList) => {
          await invalidate(invalidateList);
        }),
        invalidate(queryKeyMap.organization()),
      ]);
    },
  });
};

export const useResetPassword = () => {
  return useMutation({
    mutationFn: async (data: { id: string; newPassword: string }) => {
      const parsed = z
        .object({ id: z.string().uuid(), newPassword: z.string() })
        .parse(data);
      const { id, ...payload } = parsed;
      return await axiosPost(`/api/users/${id}/password/reset/`, payload);
    },
  });
};

export const useDownloadUserListCsv = () => {
  return useMutation({
    mutationFn: async () => {
      return await axiosPost('/api/users/csv/download/', null);
    },
  });
};

export const useUploadUserListCsv = () => {
  return useMutation({
    mutationFn: async ({
      runMode,
      csvData,
    }: z.infer<typeof BulkUpdateUsersSchema>) => {
      return await axiosPost(`/api/users/csv/upload/?run_mode=${runMode}`, {
        csvData,
      });
    },
  });
};

export const useParseUsersByIds = (userIds: string[]) => {
  const uniqueUserIds = useMemo(() => Array.from(new Set(userIds)), [userIds]);

  const { data } = useFilteredUsers({
    params: { ids: uniqueUserIds, filterBy: 'ids' },
    enabled: uniqueUserIds.length > 0,
  });

  const parseUserById = useCallback(
    (id: string): z.infer<typeof UserModel> | undefined => {
      const user = data?.results?.find((u) => u.id === id);
      return user;
    },
    [data],
  );

  const parseUsersByIds = useCallback(
    (ids: string[]): Array<z.infer<typeof UserModel>> => {
      return ids.flatMap((id) => {
        const user = parseUserById(id);
        return user ? [user] : [];
      });
    },
    [parseUserById],
  );

  return { parseUserById, parseUsersByIds };
};
